/**
 * Timing strategy for PWA installation prompts
 */

export interface EngagementMetrics {
  visits: number;
  transactions: number;
  timeSpent: number; // in milliseconds
  lastPromptShown: number | null; // timestamp
  hasBeenDismissed: boolean;
  dismissCount: number;
  cooldownPeriod: number; // in milliseconds
}

const STORAGE_KEY = 'veripay_pwa_metrics';
const DEFAULT_COOLDOWN = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Gets the current engagement metrics from localStorage
 */
export const getEngagementMetrics = (): EngagementMetrics => {
  const stored = localStorage.getItem(STORAGE_KEY);
  
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Failed to parse stored metrics', e);
    }
  }
  
  // Default metrics
  return {
    visits: 0,
    transactions: 0,
    timeSpent: 0,
    lastPromptShown: null,
    hasBeenDismissed: false,
    dismissCount: 0,
    cooldownPeriod: DEFAULT_COOLDOWN
  };
};

/**
 * Updates the engagement metrics
 */
export const updateEngagementMetrics = (updates: Partial<EngagementMetrics>): EngagementMetrics => {
  const current = getEngagementMetrics();
  const updated = { ...current, ...updates };
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
};

/**
 * Records a visit to the app
 */
export const recordVisit = (): void => {
  const metrics = getEngagementMetrics();
  console.log('Recording visit. Current visits:', metrics.visits);
  const updated = updateEngagementMetrics({ visits: metrics.visits + 1 });
  console.log('Visit recorded. Updated visits:', updated.visits);
};

/**
 * Records a transaction in the app
 */
export const recordTransaction = (): void => {
  const metrics = getEngagementMetrics();
  updateEngagementMetrics({ transactions: metrics.transactions + 1 });
};

/**
 * Records time spent in the app
 */
export const recordTimeSpent = (timeInMs: number): void => {
  const metrics = getEngagementMetrics();
  console.log('Recording time spent. Current time spent:', metrics.timeSpent, 'ms');
  const updated = updateEngagementMetrics({ timeSpent: metrics.timeSpent + timeInMs });
  console.log('Time spent recorded. Updated time spent:', updated.timeSpent, 'ms');
};

/**
 * Records that the prompt was shown
 */
export const recordPromptShown = (): void => {
  updateEngagementMetrics({ lastPromptShown: Date.now() });
};

/**
 * Records that the prompt was dismissed
 */
export const recordPromptDismissed = (): void => {
  const metrics = getEngagementMetrics();
  console.log('Recording prompt dismissal');
  updateEngagementMetrics({
    hasBeenDismissed: true,
    dismissCount: metrics.dismissCount + 1,
    lastPromptShown: Date.now()
  });
};

/**
 * Records that the user wants to be reminded later
 * Sets a shorter cooldown period than a full dismissal
 */
export const recordPromptLater = (): void => {
  const metrics = getEngagementMetrics();
  console.log('Recording "remind me later" request');
  // Set a cooldown period of 2 hours
  const laterCooldownPeriod = 2 * 60 * 60 * 1000; // 2 hours
  updateEngagementMetrics({
    lastPromptShown: Date.now(),
    cooldownPeriod: laterCooldownPeriod
  });
  console.log(`Prompt will reappear after ${laterCooldownPeriod/(60*60*1000)} hours`);
};

/**
 * Checks if we should show the installation prompt based on engagement metrics
 */
export const shouldShowPrompt = (): boolean => {
  const metrics = getEngagementMetrics();
  console.log('Checking if prompt should be shown with metrics:', metrics);
  
  // Check if we're in a cooldown period (either from dismiss or "later")
  if (metrics.lastPromptShown) {
    const timeSinceLastPrompt = Date.now() - metrics.lastPromptShown;
    console.log(`Time since last prompt: ${timeSinceLastPrompt}ms, Cooldown period: ${metrics.cooldownPeriod}ms`);
    
    if (timeSinceLastPrompt < metrics.cooldownPeriod) {
      console.log('Not showing prompt: Still in cooldown period');
      return false;
    }
    
    // For dismissals, respect multiple dismissals
    if (metrics.hasBeenDismissed && metrics.dismissCount > 3) {
      console.log('Not showing prompt: Dismissed too many times');
      return false;
    }
  }
  
  // First-time visitors: wait for engagement
//   if (metrics.visits < 2) {
//     return false;
//   }
  
//   // Show for engaged users
//   return metrics.transactions > 0 || metrics.timeSpent > 5 * 60 * 1000;

  const shouldShow = metrics.visits >= 1 || metrics.timeSpent > 10 * 1000;
  console.log(`Prompt should ${shouldShow ? '' : 'not '}be shown. Visits: ${metrics.visits}, Time spent: ${metrics.timeSpent}ms`);
  return shouldShow;
};

/**
 * Checks if the user is in a sensitive flow (e.g., payment processing)
 * This should be customized based on the application's routes and states
 */
export const inSensitiveFlow = (): boolean => {
  // Example implementation - check if the current URL contains sensitive paths
  const currentPath = window.location.pathname.toLowerCase();
  const sensitivePaths = [
    '/payment',
    '/checkout',
    '/transaction',
    '/confirm',
    '/login'
  ];
  
  return sensitivePaths.some(path => currentPath.includes(path));
};